<template>
  <div :class="$style.conditionsSection">
    <div
      v-if="!selections.length"
      :class="$style.emptyStateMessageContainer"
      :data-test-id="$testId('emptyStateMessage')"
    >
      <p :class="$style.emptyStateMessage">
        No conditions have been configured.
      </p>

      <ButtonSecondary
        :data-test-id="$testId('addCondition')"
        text="Add Condition"
        @click="handleAddNewCondition"
      />
    </div>

    <div v-else>
      <div :class="$style.inclusionOptions">
        <Select
          :class="$style.inclusionDropDown"
          :data-test-id="$testId('inclusionSelector')"
          name="Inclusion"
          :options="inclusionOptions"
          :showValidationMessage="false"
          :value="inclusion"
          @input="handleInclusionChange"
        />
        <ParagraphBody>
          of the following conditions must match for this policy to apply.
        </ParagraphBody>
      </div>

      <ConditionsTableHeader />

      <div
        v-for="row in selectedOptions"
        :key="row.id"
        :class="$style.rowContainer"
        :data-test-id="$testId('row')"
      >
        <ConditionRow
          :allOptions="options"
          :class="$style.rowSelection"
          :inclusion="inclusion"
          :menuOptions="menuOptions"
          :name="row.key"
          :selections="selections"
          :value="getCondition(row.value)"
          @change="row.changeHandler"
          @delete="(key) => unselectOption(row.key)"
          @input="row.inputHandler"
        />

        <GuideMessage
          v-if="shouldShowGuide(row)"
          caretPosition="top"
          :class="$style.conditionInfoGuide"
          name="conditionInfo"
        />
      </div> <!-- /rowContainer -->

      <ButtonSecondary
        :data-test-id="$testId('addCondition')"
        :isDisabled="shouldDisableAddButton"
        text="Add Condition"
        @click="handleAddNewCondition"
      />

      <NotificationInPage
        :class="$style.notification"
        :data-test-id="$testId('deviceWarning')"
        :notification="deviceNotification"
        :showNotification="shouldShowDevicePostureWarning"
      />

      <NotificationInPage
        :class="$style.notification"
        :data-test-id="$testId('warningCertDistributionIsDisabled')"
        :notification="certDistributionNotification"
        :showNotification="certDistributionIsDisabledWarning"
      >
        <template #message>
          <ParagraphBody>
            Device certificate distribution has been disabled.
            <span
              :class="$style.enableCertDistributionLink"
              data-test-id="warningEnableCertDistributionLink"
              @click="handleEnableCertDistributionClick"
            >
              Turn on device certificate distribution
            </span>
            or remove device conditions to ensure uninterrupted access for your users.
          </ParagraphBody>
        </template>
      </NotificationInPage>
    </div>
  </div>
</template>

<script>
import {
  ButtonSecondary,
  NotificationInPage,
  ParagraphBody,
  Select,
  Types,
} from '@jumpcloud/ui-components';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import {
  filter,
  find,
  get,
  map,
  omit,
  reduce,
  set,
  toArray,
} from 'lodash';
import { mapActions, mapState } from 'vuex';

// Components
import CertificateSettingsModal from 'client/Security/CertificateSettingsModal.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';

// Condition Row Components
import ConditionRow from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRow.vue';
import ConditionRowDevice from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRowDevice.vue';
import ConditionRowDeviceEncrypted from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRowDeviceEncrypted.vue';
import ConditionRowIpList from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRowIpList.vue';
import ConditionRowLocations from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRowLocations.vue';
import ConditionRowTemplate from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionRowTemplate.vue';
import ConditionsTableHeader from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionsTableHeader.vue';

// Conditions Types & Data
import { ConditionInclusion, ExpressionStatus } from 'client/Security/data/Condition';
import ConditionItems from 'client/Security/data/ConditionItems';

const { NotificationConfig } = Types;

export default {
  name: 'ConditionsSection',

  components: {
    ButtonSecondary,
    ConditionRow,
    ConditionsTableHeader,
    GuideMessage,
    NotificationInPage,
    ParagraphBody,
    Select,
  },

  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    const deviceNotification = NotificationConfig({
      icon: 'error',
      isMarkdown: true,
      title: 'Device Conditions',
      message: 'Device Conditions can not be enabled on unmanaged devices, which are devices without the JumpCloud agent. You can set this policy to meet **Any** of the conditions instead of **All** to include this condition in the list.',
      type: 'error',
    });

    const certDistributionNotification = NotificationConfig({
      icon: 'error',
      title: 'Device Trust Certificate Required',
      type: 'warning',
    });

    return {
      inclusion: 'all',
      expressions: {},
      selections: [],

      certDistributionNotification,
      deviceNotification,

      // tracking the initial state of cert distribution to work around the org
      // model being updated before commit (see client/Security/CertificateSettings.vue)
      certDistributionIsEnabledState: false,
      shouldShowDevicePostureWarning: false,
    };
  },

  computed: {
    ...mapState('OrganizationModel', { organization: 'item' }),
    ...mapState('SettingsModule', ['orgId']),
    ...mapState('GuidedSetupModule', ['guideName']),

    certDistributionIsDisabledWarning() {
      return (
        !this.certDistributionIsEnabledState
        && this.selections.indexOf('device') > -1
      );
    },

    certDistributionIsEnabled() {
      return get(this.organization, 'settings.deviceIdentificationEnabled', false);
    },

    inclusionOptions() {
      return [
        { name: 'All', value: ConditionInclusion.all },
        { name: 'Any', value: ConditionInclusion.any },
      ];
    },

    menuOptions() {
      return this.unselectedOptions.map(option => ({
        key: option.key,
        name: option.label,
        value: option.value,
      }));
    },

    options() {
      return {
        template: {
          key: 'template',
          label: 'Template',

          component: ConditionRowTemplate,
          value: '',

          changeHandler: (expression) => { this.handleChangeCondition('template', expression); },
          inputHandler: () => {},
          beforeSelect: () => true,

          canBeShown: false,
        },
        device: {
          key: 'device',
          label: 'Device',

          component: ConditionRowDevice,
          value: 'expressions.device',

          changeHandler: (expression) => { this.handleChangeCondition('device', expression); },
          inputHandler: (condition) => { this.setCondition('expressions.device', condition); },

          beforeSelect: () => {
            // We now do a selection on load, which sometimes is before we can update the org
            // (going direct to the url). If so, don't show the modal.
            if (!this.certDistributionIsEnabled) {
              this.openCertificateSettingsModal({
                // Here we trigger a 'refresh' of the device list since
                // the modal will update this.certDistributionIsEnabled.
                handleContinue: () => {
                  this.selectAMenuOption(this.options.device);
                  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                  this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
                },
              });
            }

            return this.certDistributionIsEnabled;
          },
          canBeShown: true,
        },
        deviceEncrypted: {
          key: 'deviceEncrypted',
          label: 'Disk Encryption',

          component: ConditionRowDeviceEncrypted,
          value: 'expressions.deviceEncrypted',

          changeHandler: (expression) => { this.handleChangeCondition('deviceEncrypted', expression); },
          inputHandler: (condition) => { this.setCondition('expressions.deviceEncrypted', condition); },

          beforeSelect: () => {
            // We now do a selection on load, which sometimes is before we can update the org
            // (going direct to the url). If so, don't show the modal.
            if (!this.certDistributionIsEnabled) {
              this.openCertificateSettingsModal({
                // Here we trigger a 'refresh' of the device list since
                // the modal will update this.certDistributionIsEnabled.
                handleContinue: () => {
                  this.selectAMenuOption(this.options.deviceEncrypted);
                  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                  this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
                },
              });
            }

            return this.certDistributionIsEnabled;
          },
          canBeShown: true,
        },
        ipAddresses: {
          key: 'ipAddresses',
          label: 'IP Address',

          component: ConditionRowIpList,
          value: 'expressions.ipAddresses',

          changeHandler: (expression) => { this.handleChangeCondition('ipAddresses', expression); },
          inputHandler: (condition) => { this.setCondition('expressions.ipAddresses', condition); },
          beforeSelect: () => true,

          canBeShown: true,
        },
        locations: {
          key: 'locations',
          label: 'Location',

          component: ConditionRowLocations,
          value: 'expressions.locations',

          changeHandler: (expression) => { this.handleChangeCondition('locations', expression); },
          inputHandler: (condition) => { this.setCondition('expressions.locations', condition); },
          beforeSelect: () => true,

          canBeShown: true,
        },
      };
    },

    selectedOptions() {
      // maintain order of selections[]
      return map(this.selections, key => this.options[key]);
    },

    shouldDisableAddButton() {
      return this.selections.includes('template') || this.unselectedOptions.length === 0;
    },

    unselectedOptions() {
      const options = omit(this.options, 'template');
      return filter(options, (option, key) => !this.selections.includes(key));
    },
  },

  watch: {
    organization: {
      handler(newVal) {
        // Settings is present after actually fetching the org, We need to know the org's settings
        // in order to correctly display or not display the warning to turn on device idenficiation.
        if ('settings' in this.organization) {
          this.certDistributionIsEnabledState = newVal.settings.deviceIdentificationEnabled;
          this.preselectCondition();
        }
      },
    },
    // Need to do this when the org loads in finally...
    // Close guided setup if condition hasn't been added (e.g. device trust w/o cert dist enabled)
    selections: {
      handler(newValue) {
        if (!newValue.includes(this.guideName)) {
          this.dismissGuide();
        }
      },
    },
    value: {
      handler(newValue) {
        // Set the condition inclusion clause.
        this.inclusion = get(newValue, 'conditions.inclusion', 'all');
        // Set the values for any existing
        const externalExpressions = get(newValue, 'conditions.expressions', {});

        this.expressions = reduce(externalExpressions, (expressions, expression, key) => (
          { ...expressions, [key]: ConditionItems[key](expression) }
        ), {});

        // Set the selected rows for any existing expressions.
        this.selections = map(this.expressions, (expression, key) => key);
      },
      immediate: true,
    },
  },

  created() {
    this.fetchOrganization(this.orgId);
  },

  beforeMount() {
    this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
  },

  mounted() {
    if ('settings' in this.organization) {
      this.preselectCondition();
    }
  },

  methods: {
    ...mapActions('OrganizationModel', { fetchOrganization: 'fetchItem' }),
    ...mapActions('GuidedSetupModule', { dismissGuide: 'dismissGuide' }),

    getCondition(key) {
      return get(this, key);
    },

    handleAddNewCondition() {
      const condition = this.unselectedOptions.length === 1
        ? this.unselectedOptions[0]
        : this.options.template;

      this.selectAMenuOption(condition);

      UiEvents.triggerButtonClicked({
        description: 'Click Add Condition button',
        page: 'Conditional Policies Aside',
        section: 'conditions',
        text: 'Add Condition',
      });
    },

    handleChangeCondition(currentOptionKey, selectedExpression) {
      const insertIndex = this.selections.indexOf(currentOptionKey);
      this.unselectOption(currentOptionKey);

      const option = find(this.options, opt => opt.value === selectedExpression);

      this.selectAMenuOption(option, insertIndex);

      UiEvents.triggerDropdownItemSelected({
        description: 'Selected condition from dropdown',
        page: 'Conditional Policies Aside',
        section: 'conditions',
        text: option.label,
      });
    },

    handleEnableCertDistributionClick() {
      this.openCertificateSettingsModal({
        handleContinue: () => {
          this.certDistributionIsEnabledState = this.certDistributionIsEnabled;
        },
      });
    },

    handleInclusionChange(value) {
      this.setCondition('inclusion', value);

      UiEvents.triggerDropdownItemSelected({
        description: 'Selected condition inclusion value',
        page: 'Conditional Policies Aside',
        section: 'conditions',
        text: value,
      });
    },

    handleValueWasUpdated() {
      this.$emit('input', {
        ...this.value,
        conditions: {
          inclusion: this.inclusion,
          expressions: toArray(
            filter(this.expressions, (expression, key) => this.selections.includes(key)),
          ),
        },
      });
    },

    openCertificateSettingsModal({ handleContinue }) {
      return ModalEventBus.open({
        isLargeModal: true,
        component: CertificateSettingsModal,
        preventCloseOnOverlayClick: true,
        componentProps: {
          handleContinue,
        },
      });
    },

    preselectCondition() {
      if (this.guideName) {
        const option = this.options[this.guideName];
        this.selectAMenuOption(option);
      }
    },

    selectAMenuOption(menuOption, index = this.selections.length) {
      const isNewOption = !this.selections.includes(menuOption.key);

      if (isNewOption && menuOption.beforeSelect()) {
        this.selections.splice(index, 0, menuOption.key);
      }
    },

    setCondition(key, value) {
      set(this, key, value);

      this.handleValueWasUpdated();
      this.validateDevicePosture();
    },

    shouldShowGuide(row) {
      const { key } = row;
      return key && this.guideName && key.toLowerCase() === this.guideName.toLowerCase();
    },

    unselectOption(optionKey) {
      this.selections = filter(this.selections, selectedOption => selectedOption !== optionKey);
      const { value } = this.options[optionKey];
      this.setCondition(value, undefined);
    },

    validateDevicePosture() {
      const deviceCondition = get(this.expressions, 'device', {});

      const isInvalid = (
        this.inclusion === ConditionInclusion.all
        && !!this.expressions.deviceEncrypted
        && deviceCondition.status === ExpressionStatus.exclude
      );

      this.shouldShowDevicePostureWarning = isInvalid;
    },
  },
};
</script>

<style module>
  @import "../../../components/FormValidation/css/formFields.css";
  @import "../../../components/FormValidation/css/FormValidationFields.css";
  @import '../../../css/sections.css';

  @value (jcInformationalBase, jcGrey800) from '~css/brand-colors.css';

  .conditionsSection {
    --vertical-margin: 1em;

    max-width: 800px;
  }

  .conditionInfoGuide {
    left: 502px;
    top: -20px;
  }

  .emptyStateMessage {
    color: jcGrey;
    font-size: 1rem;
    font-weight: 400;
    text-align: center;
  }

  .inclusionOptions {
    --vertical-margin: 2em;

    align-items: baseline;
    display: flex;
    margin-bottom: var(--vertical-margin);
  }

  .inclusionDropDown {
    margin-right: 1em;

    --input-wrapper-min-width: 6rem;
  }

  .statusLabel {
    color: jcGrey800;
    font-weight: 700;
    text-transform: capitalize;
  }

  .notification {
    margin: 2em 0;
  }

  .enableCertDistributionLink {
    color: jcInformationalBase;
    cursor: pointer;
    text-decoration: underline;
  }

  .enableCertDistributionLink:hover {
    color: jcInformationalBase;
  }
</style>
